import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {FormBuilder, FormGroup} from "@angular/forms";
import {combineLatest, Subject} from "rxjs";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {BuilderV2Service} from "../../../../builder-v2.service";
import {filter, first, tap} from "rxjs/operators";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {ReCaptchaV3Service} from "ngx-captcha";
import {
  AiAssistantFormGroup
} from "../../../../../components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant-form-data";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {User} from "@sentry/browser";
import {BuilderHistoryService} from "../../../../builder-history.service";
import {ActivatedRoute, Router} from "@angular/router";
import {BuilderStepsEnum, BuilderTourEnum} from "../../../../builder-tour.enum";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {TriggersService} from "../../../../services/triggers.service";
import {CookieService} from "ngx-cookie";
import {Config} from "../../../../../config/config";
import {VibeCodingFlowService} from "../../../../../shared/services/vibe-coding-flow.service";
import {CommonBuilderDataService} from "../../../../services/common-builder-data.service";

@Component({
    selector: 'db-create-product-categories',
    templateUrl: './create-product-categories.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './create-product-categories.component.scss',
    standalone: false
})
export class CreateProductCategoriesComponent implements OnInit, OnDestroy {

  @Input() fromVibeCoding = false;
  @Input() isEcommerce = false;
  @Input() oldFormData: Record<string, string | number | boolean | undefined> = {};
  @Input() formGroupData: AiAssistantFormGroup;
  @Input() domainId: number;
  @Input() popupMode: boolean;
  @Input() isBusinessType: boolean;
  @Input() user: User;
  @Input() uniqueId: string;
  @Output() back = new EventEmitter<boolean>();
  @Output() onMobileGenerate = new EventEmitter<boolean>();


  prefilledAi: boolean = false;
  public outlineFormGroup: FormGroup;
  public formSubmitted = false;
  public isMobile: boolean;
  public loading: boolean;
  public serverErrors: any;
  public showLoading: boolean = false;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public userLocalStorage: any;
  public generatedServices: string[] = [];
  public blacklistedServices: string[] = [];
  public collections: string[] = [];
  public inputValues: string[] = [];

  private placeholders: string[] = [
    'Please enter a product category (e.g., Shoes)',
    'Please enter a product category (e.g., Clothing)',
    'Please enter a product category (e.g., Toys)',
    'Please enter a product category (e.g., Electronics)'
  ];

  @HostBinding('class.db-mobile') get applyMobileClass() {
    return this.isMobile && !this.user && !this.popupMode;
  }

  constructor(
    private _fb: FormBuilder,
    public builderInteractionsService: BuilderSectionsInteractionService,
    private _builderV2Service: BuilderV2Service,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _builderHistoryService: BuilderHistoryService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _cdr: ChangeDetectorRef,
    private _builderTriggerService: TriggersService,
    private _vibeCodingFlowService: VibeCodingFlowService,
    private _commonDataService: CommonBuilderDataService
  ) {
    this.isEcommerce = this.builderInteractionsService.getWebsiteTypeMobile() === 'ecommerce' || this.builderInteractionsService.websiteType.value === 'ecommerce';
    this.uniqueId = this._builderHistoryService.getUniqueId();

  }

  ngOnInit() {
    this.prefilledAi = !!localStorage.getItem('prefilledAiData');
    this.isMobile = window.innerWidth <= 991;
    this.isMobile && this.checkMobileData();
    if (this.isMobile) {
      this.builderInteractionsService.setBackAction(() => {
        if (!this.popupMode && !this.user) {
          const data = this._builderHistoryService.getFormData();
          this._builderHistoryService.saveFormData(data);
          this._router.navigate([this.prefilledAi ? `../filled-company-info` : `../fill-company-info`], {relativeTo: this._route});
        } else {
          this.back.emit();
        }
      });
    }
    this._getGeneratedServices();

  }

  checkMobileData() {
    const state = this._builderHistoryService.getFormData();

    if (state) {
      this.oldFormData = {...state.oldFormData, ...state.formData};
      this.formGroupData = state.formGroupData;
      this.isBusinessType = state.isBusinessType;
      this.builderInteractionsService.loading.next(false);
    }
  }

  private sendEvent(category: string, action: string, label: string, companyType): void {
    const analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      companyType: companyType,
      websiteType: this.isBusinessType ? 'Business' : 'Ecommerce',
      uniqueId: this.uniqueId,
      isMobile: this.isMobile
    };
    if (!this.user) {
      this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData);
    } else {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
  }

  public ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private _getGeneratedServices(): void {
    this.loading = true;
    const data = this.transformOldFormData(this.oldFormData);

    this._builderV2Service.generateProducts(data, this.domainId, this.uniqueId)
      .pipe(filter((response: DbResponse): boolean => !!response), first())
      .subscribe((response) => {
        if (response.success && response.data) {
          this.generatedServices = response.data['categories'];
          this.collections = response.data['collections'];
          this.inputValues = [...this.generatedServices];
          this.builderInteractionsService.disableNextButton.next(false);
          this._cdr.detectChanges();
        } else {
          this.serverErrors = response.response ? response.response['error'] : "";
        }
        this.loading = false;
      });
  }

  getPlaceholder(index: number): string {
    return this.placeholders[index];
  }

  onServiceChange(value: string, index: number): void {
    // Update the generatedServices array with the new value
    this.inputValues[index] = value;
  }

  onGenerate() {
    this.builderInteractionsService.loading.next(true);
    const vibeCodingFlow = this._vibeCodingFlowService.isCombinedOutline();
    this._commonDataService.combinedOutline = vibeCodingFlow;

    this.sendEvent(this.popupMode ? 'Dashboard Action' : 'Onboarding', (vibeCodingFlow ? 'Vibe-coding AI Flow' : 'Section-based AI Flow') + ' - Question answered', JSON.stringify(["company_name", "company_description", "domain_name"]), this.oldFormData['business_type']);

    if (this.outlineFormGroup?.invalid) {
      this.builderInteractionsService.loading.next(false);
      this.outlineFormGroup.markAllAsTouched();
      return;
    }
    this.setCategories();
    const formValue = this._getFormValue();
    this.builderInteractionsService.siteInfo.next({
      ...formValue,
      domain_name: this.oldFormData['domainName'] as string
    });
    this.builderInteractionsService.updateProductCategories.next({
      categories: this.generatedServices,
      blacklisted_categories: this.blacklistedServices,
      collections: this.collections
    });

    if (this.isMobile) {
      const data = {siteInfo: this._getFormValue()};
      this._builderHistoryService.saveFormData(data);
      this.user ? this.onMobileGenerate.emit() : this._router.navigate(['../mobile-sign-up'], {relativeTo: this._route});
      this.user && this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_FINALIZE);
    } else {
      this._builderHistoryService.tourStep.next(BuilderTourEnum.START);
      this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);
      this._builderTriggerService.getPageMetas.next();
      combineLatest([
        this._builderV2Service.aiGenerateOutline(formValue, this.domainId, this.uniqueId)
          .pipe(tap(value => {
            if (value) {
              const vibeCodingFlow = this._vibeCodingFlowService.isCombinedOutline();
              this.sendEvent(this.popupMode ? 'Dashboard Action' : 'Onboarding', (vibeCodingFlow ? 'Vibe-coding AI Flow' : 'Section-based AI Flow') + ' - Outline Generated', '-', this.oldFormData['business_type']);
            }
          })),
        this._builderV2Service.aiGenerateStyles({...formValue}, this.domainId, this.uniqueId)
      ]).subscribe(([outline, styles]) => {
        if ((styles && !styles.success) || (outline && !outline.success)) {
          NotifierComponent.notify(new Notification(outline?.message || styles?.message, NotificationType.DANGER, true));
        }
      });
    }
  }

  private setCategories() {
    this.generatedServices = this.inputValues.filter((value, index) => {
      if (value) {
        return true;
      } else {
        this.blacklistedServices.push(this.generatedServices[index]);
        return false;
      }
    });
  }

  private _getFormValue() {
    return {
      'business_description': (this.oldFormData['store_description'] || this.oldFormData['business_description']) as string,
      'business_type': (this.oldFormData['business_type'] || this.oldFormData['industry_name']) as string,
      'business_name': (this.oldFormData['store_name'] || this.oldFormData['business_name']) as string,
      ecom_data: {
        categories: this.generatedServices,
        blacklisted_categories: this.blacklistedServices,
        collections: this.collections
      },
      'website_type': 'ecommerce'
    };
  }

  transformOldFormData(oldFormData: any): any {
    return {
      business_type: oldFormData.industry_name || oldFormData.business_type || 'string',
      business_name: oldFormData.store_name || oldFormData.business_name || 'string',
      business_description: oldFormData.store_description || oldFormData.business_description || 'string',
      website_type: 'ecommerce'
    };
  }


  private removeEventHandlers(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }
}
